import React from "react";
import { boApi, type ApiOmpBoState } from "@src/api/bo";
import type { SelectProps } from "@shared/ui/select";
import { BusinessSelect, type BusinessSelectProps } from "../BusinessSelect";
import { formatBoStateText } from "@src/components/business-data-display";
import { PickerOptionCard } from "@src/shared/ui";

/**
 * Пропсы компонента {@link BoStateSelect}.
 * @template M режим выбора: `true` для множественного выбора, `false` для одиночного.
 */
export type BoStateSelectProps<M extends boolean = false> = Omit<
    SelectProps<ApiOmpBoState, number, M>,
    "options" | "getOptionData" | "isLoading" | "isRefreshing"
> & {
    /** Код типа БО, по которому загружается список доступных статусов. */
    boType: number;
    /** Разрешает или запрещает фоновую загрузку списка статусов. */
    optionsLoadEnabled?: boolean;
    /** Время актуальности загруженных статусов в cache `react-query`. */
    optionsStaleTime?: number;
};

const getBoStateOptionData = (state: ApiOmpBoState) => ({
    value: state.Code,
    label: formatBoStateText(state),
});

type BoStateBusinessSelectChangeHandler<M extends boolean> = NonNullable<
    BusinessSelectProps<ApiOmpBoState, number, ApiOmpBoState, M>["onChange"]
>;

/**
 * Business-select для выбора статуса БО.
 *
 * Инкапсулирует загрузку `bo/state.loadListByFilter` и маппинг `ApiOmpBoState` в опции селекта.
 */
export function BoStateSelect<M extends boolean = false>({
    boType,
    value = null,
    label = "Статус БО",
    placeholder,
    fullscreenTitle = "Выберите статус(ы) БО",
    onChange,
    optionSlots,
    ...rest
}: BoStateSelectProps<M>) {
    const handleChange = React.useCallback<BoStateBusinessSelectChangeHandler<M>>(
        (_state, rawValue) => onChange(rawValue),
        [onChange],
    );

    return (
        <BusinessSelect<ApiOmpBoState, number, ApiOmpBoState, M>
            queryKey={["businessSelect", "boState", boType]}
            loadOptions={({ signal }) => boApi.states.load({ BoTypes: [boType] }, { signal })}
            getOptionData={getBoStateOptionData}
            value={value}
            label={label}
            placeholder={placeholder}
            fullscreenTitle={fullscreenTitle}
            onChange={handleChange}
            {...rest}
            optionSlots={{
                ...optionSlots,
                fullscreen:
                    optionSlots?.fullscreen ??
                    ((item) => {
                        return (
                            <PickerOptionCard
                                title={`Статус ${formatBoStateText(item)}`}
                                details={[{ label: "Краткое наименование", value: item.ShortName }]}
                            />
                        );
                    }),
            }}
        />
    );
}
